import {QueryParameters} from '../../types';
import {INewMockDataInstance} from './types';
import {IDataApi, IGet} from '../types';
import {FailureRate} from '../../../types';

const delayedPromiseResolve = <T>(data: T): Promise<T> => {
  return new Promise<T>(resolve => {
    setTimeout(() => resolve(data), 500);
  });
};

const delayedPromiseResolveVoid = (): Promise<void> => {
  return new Promise<void>(resolve => {
    setTimeout(() => resolve(), 500);
  });
};

const delayedPromiseReject = <T>(reason: any): Promise<T> => {
  return new Promise<T>((_, reject) => {
    setTimeout(() => reject(reason), 500);
  });
};

export const createMockDataApi = <
  TData extends Record<TIdKey, any>,
  TIdKey extends keyof TData = keyof TData,
  // eslint-disable-next-line @typescript-eslint/ban-types
  TParameters extends QueryParameters = {},
  TPayload = Omit<Record<string, unknown>, TIdKey>
>(
  idKey: TIdKey,
  mockData: TData[] | ((parameters: TParameters) => TData[]),
  idFactory: () => TData[TIdKey],
  newFactory?: INewMockDataInstance<TData, TIdKey, TPayload>,
  mockFailureRate?: FailureRate,
): IDataApi<TData, TIdKey, TParameters, TPayload> => {
  const mockDataStore = Array.isArray(mockData)
    ? mockData.reduce<{[id: string]: TData}>((acc, d) => {
        acc[d[idKey]] = d;
        return acc;
      }, {})
    : {};

  const resolvedMockFactory: INewMockDataInstance<
    TData,
    TIdKey,
    TPayload
  > = newFactory
    ? newFactory
    : (id: TData[TIdKey], payload: TPayload) =>
        (({[idKey]: id, ...payload} as any) as TData); // TODO: should be tight

  const requestCount: {
    [key in string]?: number;
  } = {};

  const shouldFail = (requestKey: string) => {
    requestCount[requestKey] = (requestCount[requestKey] ?? 0) + 1;
    return (
      mockFailureRate !== undefined &&
      (requestCount[requestKey] ?? 0) % mockFailureRate === 0
    );
  };

  const get: IGet<TData, TIdKey, TParameters> = (
    params?: TData[TIdKey] | TParameters,
  ) => {
    const requestKey = params ? `get:${JSON.stringify(params)}` : `get`;

    if (shouldFail(requestKey)) {
      return delayedPromiseReject<TData[]>(`Get request failed: ${requestKey}`);
    }

    if (typeof params === 'string') {
      const element = mockDataStore[params];
      if (!element) {
        return delayedPromiseReject<TData[]>('Not found');
      }
      return delayedPromiseResolve([element]);
    } else {
      if (typeof mockData === 'function' && params !== undefined) {
        const result = mockData(params);
        result.forEach(d => {
          mockDataStore[d[idKey]] = d;
        });
        return delayedPromiseResolve(result);
      } else {
        return delayedPromiseResolve(
          Object.keys(mockDataStore).map(key => mockDataStore[key]) as TData[],
        );
      }
    }
  };

  const update = (id: TData[TIdKey], data: Partial<Omit<TData, TIdKey>>) => {
    if (shouldFail('update')) {
      return delayedPromiseReject<void>('Update request failed');
    }

    mockDataStore[id] = {...mockDataStore[id], ...data};
    return delayedPromiseResolveVoid();
  };

  const remove = (id: TData[TIdKey]) => {
    if (shouldFail('remove')) {
      return delayedPromiseReject<void>('Remove request failed');
    }

    delete mockDataStore[id];
    return delayedPromiseResolveVoid();
  };

  const create = (payload: TPayload): Promise<TData> => {
    if (shouldFail('create')) {
      return delayedPromiseReject('Mock should fail: Create request failed');
    }
    const newElement = resolvedMockFactory(idFactory(), payload);
    mockDataStore[newElement[idKey]] = newElement;
    return delayedPromiseResolve(newElement);
  };
  return {get, update, remove, create};
};
